import { useMemo, useState, type ReactElement } from 'react';
import { ClipboardList, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  DataTable,
  DataTableHeaderRow,
  DataTableTable,
  DataTableTbody,
  DataTableTd,
  DataTableTh,
  DataTableThead,
  DataTableTr,
} from '@/components/ui/data-table';
import { FetchErrorCallout } from '@/components/ui/fetch-error-callout';
import { Skeleton } from '@/components/ui/skeleton';
import { PageHeader } from '@/components/shell/PageHeader';

import {
  useAdminQuestionnaireTemplateMutations,
  useAdminQuestionnaireTemplates,
  useQuestionnaireDeliveryMethods,
  type AdminQuestionnaireTemplateInput,
  type AdminQuestionnaireTemplateViewModel,
  type UpdateAdminQuestionnaireTemplateInput,
} from '@/features/admin-questionnaire-templates/api';
import { AdminQuestionnaireTemplatesTable } from '@/features/admin-questionnaire-templates/components/AdminQuestionnaireTemplatesTable';
import { PpqApplicationUrlSettings } from '@/features/admin-questionnaire-templates/components/PpqApplicationUrlSettings';
import { PedigreeCreationSettings } from '@/features/admin-questionnaire-templates/components/PedigreeCreationSettings';
import { CreateQuestionnaireTemplateDialog } from '@/features/admin-questionnaire-templates/components/CreateQuestionnaireTemplateDialog';
import { DeleteQuestionnaireTemplateConfirmDialog } from '@/features/admin-questionnaire-templates/components/DeleteQuestionnaireTemplateConfirmDialog';
import { EditQuestionnaireTemplateDialog } from '@/features/admin-questionnaire-templates/components/EditQuestionnaireTemplateDialog';

export function AdminQuestionnairesPage(): ReactElement {
  const { t } = useTranslation();
  const query = useAdminQuestionnaireTemplates();
  const deliveryMethods = useQuestionnaireDeliveryMethods();
  const { create, update, remove } = useAdminQuestionnaireTemplateMutations();

  const [createOpen, setCreateOpen] = useState(false);
  const [pendingEdit, setPendingEdit] = useState<AdminQuestionnaireTemplateViewModel | null>(null);
  const [pendingDelete, setPendingDelete] = useState<AdminQuestionnaireTemplateViewModel | null>(null);

  const rows = useMemo(
    () => (query.isSuccess ? [...query.data].sort((a, b) => a.label.localeCompare(b.label)) : []),
    [query.isSuccess, query.data],
  );

  function handleCreate(input: AdminQuestionnaireTemplateInput): void {
    create.mutate(input, {
      onSuccess: () => {
        setCreateOpen(false);
        create.reset();
      },
    });
  }

  function handleEditSubmit(input: UpdateAdminQuestionnaireTemplateInput): void {
    update.mutate(input, {
      onSuccess: () => {
        setPendingEdit(null);
        update.reset();
      },
    });
  }

  function handleDeleteConfirm(row: AdminQuestionnaireTemplateViewModel): void {
    remove.mutate(row.id, {
      onSuccess: () => {
        setPendingDelete(null);
        remove.reset();
      },
    });
  }

  return (
    <>
      <PageHeader
        icon={<ClipboardList className="h-[18px] w-[18px]" aria-hidden="true" />}
        title={t('routes.admin.questionnaires.heading')}
        subtitle={t('routes.admin.questionnaires.subheading')}
      />
      <div className="px-8 py-5 space-y-8 bg-background">
        <PpqApplicationUrlSettings />

        <PedigreeCreationSettings />

        <section aria-labelledby="admin-questionnaire-templates-heading" className="space-y-4">
          <header className="flex items-start justify-between gap-4">
            <div>
              <h2 id="admin-questionnaire-templates-heading" className="text-base font-semibold text-foreground">
                {t('routes.admin.questionnaires.templates.heading')}
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                {t('routes.admin.questionnaires.templates.subheading')}
              </p>
            </div>
            <Button
              size="sm"
              onClick={() => {
                create.reset();
                setCreateOpen(true);
              }}
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              {t('routes.admin.questionnaires.templates.addAction')}
            </Button>
          </header>

          {query.isPending && <RowsSkeleton />}

          {query.isError && (
            <div className="max-w-xl">
              <FetchErrorCallout
                error={query.error}
                heading={t('routes.admin.questionnaires.templates.error.heading')}
                message={t('routes.admin.questionnaires.templates.error.message')}
                codeLabel={t('routes.admin.questionnaires.templates.error.codeLabel')}
                onRetry={() => {
                  query.refetch().catch(() => undefined);
                }}
                retryLabel={t('routes.admin.questionnaires.templates.error.retry')}
              />
            </div>
          )}

          {query.isSuccess && rows.length === 0 && (
            <p className="text-sm text-muted-foreground" role="status">
              {t('routes.admin.questionnaires.templates.empty')}
            </p>
          )}

          {query.isSuccess && rows.length > 0 && (
            <DataTable aria-label={t('routes.admin.questionnaires.templates.heading')}>
              <AdminQuestionnaireTemplatesTable
                rows={rows}
                onEdit={(row) => {
                  update.reset();
                  setPendingEdit(row);
                }}
                onDelete={(row) => {
                  remove.reset();
                  setPendingDelete(row);
                }}
                onToggleActive={(row) => {
                  update.mutate({ ...row, active: !row.active });
                }}
                pendingDeleteId={remove.isPending && pendingDelete !== null ? pendingDelete.id : null}
                pendingUpdateId={update.isPending ? update.variables.id : null}
              />
            </DataTable>
          )}
        </section>

        <section aria-labelledby="admin-questionnaire-delivery-heading" className="space-y-4">
          <div>
            <h2 id="admin-questionnaire-delivery-heading" className="text-base font-semibold text-foreground">
              {t('routes.admin.questionnaires.delivery.heading')}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {t('routes.admin.questionnaires.delivery.subheading')}
            </p>
          </div>

          {deliveryMethods.isPending && <RowsSkeleton />}

          {deliveryMethods.isError && (
            <div className="max-w-xl">
              <FetchErrorCallout
                error={deliveryMethods.error}
                heading={t('routes.admin.questionnaires.delivery.error.heading')}
                message={t('routes.admin.questionnaires.delivery.error.message')}
                codeLabel={t('routes.admin.questionnaires.templates.error.codeLabel')}
                onRetry={() => {
                  deliveryMethods.refetch().catch(() => undefined);
                }}
                retryLabel={t('routes.admin.questionnaires.templates.error.retry')}
              />
            </div>
          )}

          {deliveryMethods.isSuccess && (
            <DataTable aria-label={t('routes.admin.questionnaires.delivery.heading')}>
              <DataTableTable aria-label={t('routes.admin.questionnaires.delivery.heading')}>
                <DataTableThead className="sticky top-0 z-10 bg-card">
                  <DataTableHeaderRow>
                    <DataTableTh className="h-[40px] py-0" widthClassName="w-[30%] min-w-[6rem]">
                      {t('routes.admin.questionnaires.delivery.table.id')}
                    </DataTableTh>
                    <DataTableTh className="h-[40px] py-0">
                      {t('routes.admin.questionnaires.delivery.table.label')}
                    </DataTableTh>
                  </DataTableHeaderRow>
                </DataTableThead>
                <DataTableTbody>
                  {deliveryMethods.data.map((method) => (
                    <DataTableTr key={method.id} className="even:bg-muted/55">
                      <DataTableTd className="h-[40px] py-0 font-mono text-xs text-muted-foreground" dataOffset={false}>
                        {method.id}
                      </DataTableTd>
                      <DataTableTd className="h-[40px] py-0 text-foreground/90" dataOffset>
                        {method.label}
                      </DataTableTd>
                    </DataTableTr>
                  ))}
                </DataTableTbody>
              </DataTableTable>
            </DataTable>
          )}
        </section>
      </div>

      <CreateQuestionnaireTemplateDialog
        open={createOpen}
        onOpenChange={(next) => {
          if (!next && create.isPending) return;
          if (!next) create.reset();
          setCreateOpen(next);
        }}
        onSubmit={handleCreate}
        pending={create.isPending}
        submitError={create.error}
      />

      <EditQuestionnaireTemplateDialog
        row={pendingEdit}
        onCancel={() => {
          if (update.isPending) return;
          setPendingEdit(null);
          update.reset();
        }}
        onSubmit={handleEditSubmit}
        pending={update.isPending}
        submitError={update.error}
      />

      <DeleteQuestionnaireTemplateConfirmDialog
        row={pendingDelete}
        onCancel={() => {
          if (remove.isPending) return;
          setPendingDelete(null);
          remove.reset();
        }}
        onConfirm={handleDeleteConfirm}
        pending={remove.isPending}
        submitError={remove.error}
      />
    </>
  );
}

function RowsSkeleton(): ReactElement {
  return (
    <div className="space-y-2" aria-hidden="true">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} className="h-10 w-full rounded-md" />
      ))}
    </div>
  );
}
